<template>
  <v-form
    v-bind="$attrs"
    :model-value="modelValue"
    @update:model-value="updateModelValue"
    @submit.prevent="handleSubmit"
    :class="formClasses"
    class="ds-form"
    ref="formRef"
  >
    <!-- Form Title -->
    <div v-if="title || $slots.title" class="ds-form__header">
      <slot name="title">
        <h3 class="ds-form__title">{{ title }}</h3>
      </slot>
      <p v-if="description || $slots.description" class="ds-form__description">
        <slot name="description">
          {{ description }}
        </slot>
      </p>
    </div>

    <!-- Form Fields -->
    <div :class="fieldsContainerClasses" class="ds-form__fields">
      <slot />
    </div>

    <!-- Form Alert/Message -->
    <div v-if="errorMessage || successMessage || $slots.message" class="ds-form__message">
      <slot name="message">
        <ds-alert
          v-if="errorMessage"
          type="error"
          :message="errorMessage"
          closable
          @close="clearErrorMessage"
        />
        <ds-alert
          v-if="successMessage"
          type="success"
          :message="successMessage"
          closable
          @close="clearSuccessMessage"
        />
      </slot>
    </div>

    <!-- Form Actions -->
    <div v-if="showActions || $slots.actions" :class="actionsClasses" class="ds-form__actions">
      <slot name="actions">
        <ds-button
          v-if="showCancel"
          :ds-variant="cancelVariant"
          :disabled="loading"
          @click="handleCancel"
        >
          {{ cancelText }}
        </ds-button>

        <v-spacer v-if="actionsAlign === 'space-between'" />

        <ds-button
          v-if="showReset"
          ds-variant="ghost"
          :disabled="loading"
          @click="handleReset"
        >
          {{ resetText }}
        </ds-button>

        <ds-button
          v-if="showSubmit"
          :ds-variant="submitVariant"
          :loading="loading"
          type="submit"
        >
          {{ submitText }}
        </ds-button>
      </slot>
    </div>
  </v-form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { DsButton } from '@/shared/components/atoms'
import { DsAlert } from '@/shared/components/molecules'

export interface DsFormProps {
  /**
   * Estado de validación del formulario
   */
  modelValue?: boolean
  /**
   * Título del formulario
   */
  title?: string
  /**
   * Descripción del formulario
   */
  description?: string
  /**
   * Mostrar acciones
   * @default true
   */
  showActions?: boolean
  /**
   * Mostrar botón de envío
   * @default true
   */
  showSubmit?: boolean
  /**
   * Texto del botón de envío
   * @default 'Enviar'
   */
  submitText?: string
  /**
   * Variante del botón de envío
   * @default 'primary'
   */
  submitVariant?: 'primary' | 'secondary' | 'ghost' | 'text'
  /**
   * Mostrar botón de cancelar
   * @default false
   */
  showCancel?: boolean
  /**
   * Texto del botón cancelar
   * @default 'Cancelar'
   */
  cancelText?: string
  /**
   * Variante del botón cancelar
   * @default 'ghost'
   */
  cancelVariant?: 'primary' | 'secondary' | 'ghost' | 'text'
  /**
   * Mostrar botón de resetear
   * @default false
   */
  showReset?: boolean
  /**
   * Texto del botón resetear
   * @default 'Limpiar'
   */
  resetText?: string
  /**
   * Estado de carga
   * @default false
   */
  loading?: boolean
  /**
   * Alineación de las acciones
   * @default 'end'
   */
  actionsAlign?: 'start' | 'center' | 'end' | 'space-between'
  /**
   * Layout de los campos
   * @default 'vertical'
   */
  fieldsLayout?: 'vertical' | 'grid-2' | 'grid-3' | 'grid-4'
  /**
   * Gap entre campos
   * @default 'md'
   */
  fieldsGap?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Mensaje de error
   */
  errorMessage?: string
  /**
   * Mensaje de éxito
   */
  successMessage?: string
}

const props = withDefaults(defineProps<DsFormProps>(), {
  showActions: true,
  showSubmit: true,
  submitText: 'Enviar',
  submitVariant: 'primary',
  showCancel: false,
  cancelText: 'Cancelar',
  cancelVariant: 'ghost',
  showReset: false,
  resetText: 'Limpiar',
  loading: false,
  actionsAlign: 'end',
  fieldsLayout: 'vertical',
  fieldsGap: 'md'
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean | null]
  'submit': []
  'cancel': []
  'reset': []
  'clear-error': []
  'clear-success': []
}>()

const formRef = ref<any>(null)

// Form classes
const formClasses = computed(() => ({
  'ds-form--loading': props.loading
}))

// Fields container classes
const fieldsContainerClasses = computed(() => ({
  [`ds-form__fields--${props.fieldsLayout}`]: true,
  [`ds-form__fields--gap-${props.fieldsGap}`]: true
}))

// Actions classes
const actionsClasses = computed(() => ({
  [`ds-form__actions--${props.actionsAlign}`]: true
}))

// Update model value
const updateModelValue = (value: boolean | null) => {
  emit('update:modelValue', value)
}

// Handlers
const handleSubmit = async () => {
  if (formRef.value) {
    const { valid } = await formRef.value.validate()
    if (valid) {
      emit('submit')
    }
  } else {
    emit('submit')
  }
}

const handleCancel = () => {
  emit('cancel')
}

const handleReset = () => {
  if (formRef.value) {
    formRef.value.reset()
  }
  emit('reset')
}

const clearErrorMessage = () => {
  emit('clear-error')
}

const clearSuccessMessage = () => {
  emit('clear-success')
}

// Expose methods
defineExpose({
  validate: async () => {
    if (formRef.value) {
      return await formRef.value.validate()
    }
    return { valid: true }
  },
  reset: () => {
    if (formRef.value) {
      formRef.value.reset()
    }
  },
  resetValidation: () => {
    if (formRef.value) {
      formRef.value.resetValidation()
    }
  }
})
</script>

<style scoped lang="scss">
.ds-form {
  &__header {
    margin-bottom: 24px;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 600;
    color: rgb(var(--v-theme-gray-900));
    margin-bottom: 8px;
  }

  &__description {
    font-size: 0.875rem;
    color: rgb(var(--v-theme-gray-600));
    line-height: 1.5;
  }

  &__fields {
    display: flex;
    flex-direction: column;

    &--vertical {
      flex-direction: column;
    }

    &--grid-2 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
    }

    &--grid-3 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
    }

    &--grid-4 {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
    }

    &--gap-none {
      gap: 0;
    }

    &--gap-sm {
      gap: 8px;
    }

    &--gap-md {
      gap: 16px;
    }

    &--gap-lg {
      gap: 24px;
    }

    &--gap-xl {
      gap: 32px;
    }

    @media (max-width: 960px) {
      &--grid-2,
      &--grid-3,
      &--grid-4 {
        grid-template-columns: 1fr;
      }
    }
  }

  &__message {
    margin: 16px 0;
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;

    &--start {
      justify-content: flex-start;
    }

    &--center {
      justify-content: center;
    }

    &--end {
      justify-content: flex-end;
    }

    &--space-between {
      justify-content: space-between;
    }
  }

  &--loading {
    pointer-events: none;
    opacity: 0.6;
  }
}
</style>
